import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import type { SignInLayoutProps } from './SignInLayout.types';
import { SignInTextLink } from './SignInTextLink';

/**
 * The sign-in card's composition — web and mobile (Figma 29991:39343,
 * "UX/UI WebApp").
 *
 * This block owns the ORDER and the SPACING and nothing else; every piece
 * that has to know about a platform (which button, which field, which
 * navigation) arrives as a slot. Social sign-in sits ABOVE the email form
 * because most people arrive with a Google or Apple account and should not
 * have to read past a form to find that out.
 *
 * Vertical rhythm, top to bottom (Figma, in DS points):
 *
 *   header                     (Ka tile · title · subtitle)
 *     ↓ 36
 *   social      column, 12 apart
 *     ↓ 28
 *   divider     line · label · line
 *     ↓ 28
 *   fields      column, 12 apart
 *     ↓ 8       forgot-password row, right-aligned — ONLY when passed
 *     ↓ 28
 *   action      the 48px primary CTA
 *     ↓ 16
 *   footer      the legal consent line
 *
 * Each step is a MARGIN on the section below it rather than one `gap` on the
 * column, because the steps differ.
 *
 * `social` and `fields` arrive as fragments — the block wraps each in its own
 * gap column, so a caller that brings its own wrapper gets a doubled gap.
 */
export function SignInLayout({
  header,
  social,
  dividerLabel,
  fields,
  forgotPassword,
  action,
  footer,
  className,
  testID,
}: SignInLayoutProps) {
  return (
    <View className={cn('w-full', className)} testID={testID}>
      {header}

      <View className="mt-9 w-full gap-3">{social}</View>

      <View className="mt-7 w-full flex-row items-center gap-3">
        <View className="h-px flex-1 bg-secondary-200" />
        {/* Figma Caption S: 12 medium, secondary-400, uppercased. The label
            arrives pre-translated in its own case ("or" / "oder" / "או"), so
            the uppercasing is the DS's, not the translator's. */}
        <Text className="text-b3 font-medium uppercase text-secondary-400 native:font-mona-medium">
          {dividerLabel}
        </Text>
        <View className="h-px flex-1 bg-secondary-200" />
      </View>

      <View className="mt-7 w-full gap-3">{fields}</View>

      {/* Absent, not disabled, until the call site says so — see
          `forgotPasswordAvailable` in @flashka/shared/auth. */}
      {forgotPassword ? (
        <View className="mt-2 w-full flex-row justify-end">
          <SignInTextLink {...forgotPassword} />
        </View>
      ) : null}

      <View className="mt-7 w-full">{action}</View>
      <View className="mt-4 w-full">{footer}</View>
    </View>
  );
}
(SignInLayout as { displayName?: string }).displayName = 'SignInLayout';
